import { useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { LoadingOverlay } from '@givebutter/primitives/loading-overlay';
import { useToast } from '@givebutter/primitives/toast';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ItemRequest, extendCampaignItem, updateCampaignItem } from 'api/campaigns';
import * as Dialog from 'components/dialog';
import * as Form from 'components/form';
import { useAuctionSettings } from 'components/pages/campaign-settings/campaign-auction/auction-settings/useAuctionSettings';
import { ItemOptionsContext } from 'components/pages/campaign-settings/campaign-auction/items-form/ItemOptionsContext';
import { useItemForm } from 'components/pages/campaign-settings/campaign-auction/items-form/useItemForm';
import { dateFromServer, dateToServerDateTime } from 'helpers/dates';
import { DateTime } from 'luxon';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { GivebutterAxiosError } from 'types/error';

type ExtendItemForm = { endDate: [DateTime] };

interface ExtendItemDialogProps extends Dialog.DialogProps {}

const ExtendItemDialog = (props: ExtendItemDialogProps) => {
    const { children, ...rest } = props;
    const toast = useToast();
    const { item, campaign } = useContext(ItemOptionsContext);
    const { auction, updateAuctionMutation } = useAuctionSettings();
    const history = useHistory();
    const queryClient = useQueryClient();
    const { itemRoutes } = useItemForm();

    const now = DateTime.now();
    const nextSlot = () =>
        now.set({
            minute: Math.ceil(now.get('minute') / 15) * 15 + 15,
        });

    const initialEndDate = dateFromServer(auction?.end_at || item.end_at || now.toString());

    const methods = Form.useGivebutterForm<ExtendItemForm>({
        defaultValues: {
            endDate: [initialEndDate < now ? nextSlot() : initialEndDate],
        },
    });

    const { control, path, setError } = methods;

    const minDate = nextSlot();

    const hasCustomBiddingDateRange = !!item?.start_at_raw;
    const campaignId = campaign?.id!;

    const { mutateAsync: updateAuction, isSuccess } = updateAuctionMutation;

    const { mutate, isPending: isLoading } = useMutation({
        mutationFn: async (endDate: DateTime) => {
            const end_at = dateToServerDateTime(endDate);
            let promise = Promise.resolve();
            if (!auctionActive) {
                promise = updateAuction({
                    auctionId: auction?.id!,
                    campaignId: campaign?.id!,
                    data: { end_at },
                });
            }
            return promise
                .then(() =>
                    extendCampaignItem({
                        campaignId,
                        itemId: item?.id,
                        end_at,
                    }),
                )
                .then(() =>
                    updateCampaignItem({
                        campaignId,
                        itemId: item?.id,
                        data: {
                            ...item,
                            end_at: hasCustomBiddingDateRange ? end_at : null,
                            is_published: true,
                        } as ItemRequest,
                    }),
                );
        },
        onError: (error: GivebutterAxiosError) => {
            const errors = error.response?.data.errors;
            toast.error(error.response?.data?.message ?? `An unknown error occurred.`);
            if (errors) {
                setError('endDate', {
                    message: errors.end_at[0],
                });
            }
        },
        onSuccess: ({ id }) => {
            toast.success('Item extended successfully!');
            queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.item(campaignId, id),
            });
            queryClient.invalidateQueries({ queryKey: campaignQueryKeys.items(campaignId) });
            history.push(itemRoutes.show(id));
        },
    });

    const auctionActive =
        !isSuccess && !!auction?.end_at && dateFromServer(auction.end_at) > DateTime.now();

    const onSubmit = ({ endDate: [value] }: { endDate: [DateTime] }) => mutate(value);

    return (
        <Dialog.Root {...rest}>
            {children}
            <Dialog.Portal>
                <Dialog.Overlay />
                <Dialog.Content>
                    <Dialog.Close />
                    <Dialog.Body>
                        <LoadingOverlay isLoading={isLoading}>
                            <FontAwesomeIcon
                                className="bg-orange-25 rounded-full p-3 text-2xl text-orange-500"
                                icon={faExclamationTriangle}
                            />
                            <Form.GivebutterFormProvider {...methods}>
                                <form
                                    className="flex flex-col justify-start gap-6"
                                    onSubmit={methods.handleSubmit(onSubmit)}
                                >
                                    <div className="flex flex-col gap-2">
                                        <h3 className="text-xl font-semibold">
                                            {!auctionActive
                                                ? 'Extend item and auction?'
                                                : 'Extend item?'}
                                        </h3>
                                        {!auctionActive ? (
                                            <p>
                                                Your auction has ended. To extend this item, set a
                                                new end date and extend the auction.
                                            </p>
                                        ) : (
                                            hasCustomBiddingDateRange && (
                                                <p>To extend this item, set a new end date.</p>
                                            )
                                        )}
                                        {(!auctionActive || hasCustomBiddingDateRange) && (
                                            <>
                                                <div className="flex justify-stretch gap-2">
                                                    <Form.UseFormDateTimePicker
                                                        startTimeDisabledUntilEnabled
                                                        control={control}
                                                        name={path('endDate')}
                                                        allowRange={false}
                                                        minDate={minDate}
                                                        startDateTimeProps={{
                                                            startInputProps: {
                                                                placeholder: 'Select date & time',
                                                            },
                                                        }}
                                                        rules={{
                                                            validate: {
                                                                minDate: ([value]) =>
                                                                    value > DateTime.now() ||
                                                                    'End date must be in the future.',
                                                            },
                                                        }}
                                                    />
                                                </div>
                                                <Form.UseFormError name={path('endDate')} />
                                            </>
                                        )}
                                    </div>
                                    <div className="grid grid-flow-col gap-4">
                                        <Dialog.Cancel asChild>
                                            <Button variant="outline" color="neutral">
                                                Cancel
                                            </Button>
                                        </Dialog.Cancel>
                                        <Form.SubmitButton>Submit</Form.SubmitButton>
                                    </div>
                                </form>
                            </Form.GivebutterFormProvider>
                        </LoadingOverlay>
                    </Dialog.Body>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

const ExtendItemTrigger = Dialog.Trigger;

const Root = ExtendItemDialog;
const Trigger = ExtendItemTrigger;

export { Root, Trigger, ExtendItemDialog, ExtendItemTrigger };
